/**
 * ElecBid — ROUTE PERMISSION REGISTRY  ★ single source of truth ★
 *
 * Every route in App.jsx must have an entry here. `scripts/check-route-registry.js`
 * runs as `prebuild` and FAILS THE VERCEL BUILD if one is missing, so a new tab
 * cannot ship without someone deciding who is allowed to see it.
 *
 * WHY THIS EXISTS
 * Permissions used to be decided in two places that drifted apart: a `show:`
 * expression in Layout.jsx (which only hid the nav LINK) and — usually — nothing at
 * all on the route itself. So "gated" meant "the link is hidden", and typing the URL
 * walked straight in. Worse, the default was OPEN: a new nav item defaulted to
 * `show: true`, and featureAllowed() returned true for any path missing from
 * FEATURE_BY_PATH. Manage Crew then displayed tabs as locked "adminOnly" that were
 * not actually enforced anywhere, so the screen you'd use to reason about access was
 * itself wrong. One entry per route, consumed by both the route guard and the nav,
 * makes that class of drift impossible.
 *
 * WHAT THIS IS NOT
 * This is NOT a security boundary. It is client-side JavaScript; anyone who opens
 * devtools can bypass it. The real boundaries are Postgres RLS and the api/ auth
 * gates. This layer stops honest people from seeing things they shouldn't, and stops
 * a tab from silently becoming visible to everyone. Never let it be the only thing
 * standing between crew and sensitive data.
 *
 * TIERS
 *   public  — any signed-in, active crew member
 *   crew    — requires ANY ONE of `keys` (owner/admin always pass, per hasPermission)
 *   admin   — role admin or owner
 *   owner   — role owner only
 *   super   — is_superadmin only
 *
 * The tiers below deliberately MIRROR the `show:` conditions Layout.jsx had on
 * 2026-07-16, so shipping the registry changes nothing about who sees which nav
 * link. The change is that the ROUTE is now enforced too. Tightening beyond current
 * behaviour is a separate, deliberate decision — don't smuggle it in here.
 */

// ── Tier resolution ─────────────────────────────────────────────────────────
// Mirrors usePermissions.hasPermission(): owner/admin pass every `crew:` key.
export function allows(entry, perms) {
  if (!entry) return false; // unknown route → DENY. The whole point.
  const {
    role,
    permissions = {},
    isSuperadmin = false,
    realIsOwner = false,
  } = perms || {};

  const isOwner = role === "owner";
  const isAdmin = role === "admin" || isOwner;

  switch (entry.tier) {
    case "public":
      return true;
    case "super":
      return !!isSuperadmin;
    case "owner":
      // useReal: an owner in "View As X" mode still reaches this route. Manage Crew
      // relies on it (ManageCrew.jsx guards on realIsOwner for the same reason) —
      // otherwise the owner would impersonate someone and lose the exit hatch.
      return entry.useReal ? !!realIsOwner || isOwner : isOwner;
    case "admin":
      return isAdmin;
    case "crew":
      if (isAdmin) return true;
      return (entry.keys || []).some((k) => !!permissions[k]);
    default:
      return false; // unknown tier → DENY (a typo must never open a door)
  }
}

export function canAccessPath(path, perms) {
  return allows(ROUTES[path], perms);
}

// ── The registry ────────────────────────────────────────────────────────────
// Keep keys in sync with ManageCrew's grantable permission keys.
export const ROUTES = {
  // — Core crew surfaces. Nothing here carries revenue, profit, wage or supplier
  //   cost data. HomeScreen does show client name/phone/email/address, which crew
  //   need to actually do the job.
  "/": { tier: "public", label: "Requests" },
  // My Schedule: the crew's own day — visits + tasks off the Supabase mirrors,
  // notes/photos queue, native clock in/out. No money data; every read/write is
  // caller-scoped server-side in /api/crew-schedule, so any active crew member
  // may see it (that's the point — it must work for the whole field crew when
  // Jobber is down).
  "/schedule": { tier: "public", label: "My Schedule" },
  // Generator maintenance: the field tool for Colton's annual standby-generator
  // report (BE-GEN-MNT-01). Same audience and reasoning as /schedule — a tech has
  // to be able to fill it standing at the unit, it carries no revenue/margin data,
  // and every read/write is caller- and org-scoped inside
  // /api/generator-maintenance. Client name + site address only, which crew
  // already see on HomeScreen because they need it to do the job.
  "/generator-report": { tier: "public", label: "Generator Maintenance" },
  "/generator-report/:reportId": { tier: "public", label: "Generator Report" },
  // Panel Schedule Designer (2026-09-23): same reasoning as /generator-report —
  // a tech fills the circuit directory standing at the panel; it carries client
  // name + site address only; every read/write is org-scoped inside
  // /api/panel-schedule (requireCrew). Per-company flag: "calculators".
  "/panel-schedule": { tier: "public", label: "Panel Schedule" },
  "/panel-schedule/:scheduleId": { tier: "public", label: "Panel Schedule" },
  // SOPs (2026-10-13): the company's written procedures — note prompts, daily
  // routine, how-we-do-it. Every active crew member must be able to read them;
  // that is the point. /api/sops (requireCrew, org-scoped) decides which rows a
  // caller gets: office-only procedures and drafts go to admin/owner only, and
  // editing is requireAdmin. No money or client data lives here.
  "/sops": { tier: "public", label: "SOPs" },
  "/sops/:slug": { tier: "public", label: "SOP" },
  // Permits & Hydro guide (2026-11-04): the office SOP for TSBC permits, BC
  // Hydro connections and what the crew sends at each stage. Same audience
  // and reasoning as /sops — procedures every active crew member may read.
  // Fee numbers shown are TSBC's published schedule, not our pricing.
  "/permit-guide": { tier: "public", label: "Permits & Hydro" },
  // Permits board (2026-11-04): the live TSBC permit register joined to Jobber
  // jobs — client names, site addresses and job links, the same exposure as the
  // jobs surfaces, so it rides the same view_jobs key (lead techs are the ones
  // who owe the declarations). Import/link actions are admin-gated server-side.
  "/permits": { tier: "crew", keys: ["view_jobs"], label: "Permits" },
  // Generator Sales Playbook (2026-10-28): the sales-training booklet — real
  // generator installs by scenario with what each sold for, quoted vs actual van
  // hours and the job's gross profit. It carries prices and margins, so it sits
  // behind the same key as /quotes (Dylan 2026-07-22: apprentices shouldn't see
  // quotes). Photos come from /api/job-media (requireCrew, org-scoped).
  "/generator-playbook": { tier: "crew", keys: ["view_quotes"], label: "Generator Playbook" },
  // Quotes carry pricing, labour rates and margins — permission-gated 2026-07-22
  // (Dylan: apprentices shouldn't see quotes). Existing crew rows were backfilled
  // with view_quotes=true so nothing changed at deploy; revoke per-person in
  // Manage Crew. Takeoff/pricelist stay public — techs need them in the field.
  "/quotes": { tier: "crew", keys: ["view_quotes"], label: "Quotes" },
  "/takeoff": { tier: "public", label: "Material Takeoff" },
  "/pricelist": { tier: "public", label: "Price List" },
  // /feedback is App.jsx's fallback landing route — a crew member with no other
  // permitted surface is redirected here. It must never be tier-gated or such an
  // account has nowhere to land (redirect loop / dead app).
  "/feedback": { tier: "public", label: "Feedback" },
  "/assess/smart/:id": { tier: "public", label: "Smart Assessment" },
  // Same audience as /quotes — a quote draft is where the pricing actually is.
  "/quote/:id": { tier: "crew", keys: ["view_quotes"], label: "Quote Review" },
  "/quote/legacy/:id": { tier: "crew", keys: ["view_quotes"], label: "Quote Builder (legacy)" },
  // OAuth return leg — must stay reachable or reconnecting Jobber breaks.
  "/jobber/callback": { tier: "public", label: "Jobber OAuth callback" },
  // Redirects into /quotes, which does its own sub-tab gating.
  "/quote-templates": { tier: "public", label: "Quote Templates (redirect)" },
  "/quote-db": { tier: "public", label: "Backend Library (redirect)" },
  "/backend-builder": { tier: "public", label: "Backend Builder (redirect)" },

  // — Permission-gated crew surfaces (mirrors the old Layout `show:` conditions).
  "/calculators": { tier: "crew", keys: ["view_calculators"], label: "Calculators" },
  "/crew": { tier: "crew", keys: ["view_crew_metrics"], label: "Crew Performance" },
  // Jobs requires view_jobs — FULL STOP. The old legacy fallback also accepted
  // view_crew_metrics, which meant unchecking "Jobs" in Manage Crew did nothing:
  // every crew member with the Crew tab (Morgan, Landon, Lorne, Pippin — all
  // view_jobs=false) still saw Jobs, revenue and invoice rollups included.
  // Dylan 2026-07-23. Jobs carries per-job revenue/profit, so it stays gated.
  "/jobs": { tier: "crew", keys: ["view_jobs"], label: "Jobs" },
  // Per-job workspace in the flow-through chain — same audience as /jobs (it
  // shows the same revenue/invoice rollups, plus visits/expenses/notes).
  "/job/:jobNum": { tier: "crew", keys: ["view_jobs"], label: "Job Detail" },
  "/expenses": { tier: "crew", keys: ["view_expenses"], label: "Expenses" },
  "/reconciliation": { tier: "crew", keys: ["view_expenses"], label: "Reconciliation" },
  "/marketing": { tier: "crew", keys: ["view_marketing"], label: "Marketing" },
  "/comms": { tier: "crew", keys: ["view_marketing"], label: "Communications" },
  "/fleet": { tier: "crew", keys: ["view_fleet"], label: "Fleet" },

  // — Admin surfaces: financials, client/candidate PII, and Jobber write tools.
  // ElecBid Pipeline: the all-in-one command center + client-comms scheduler.
  // Client PII + the ability to text/email customers → admin/owner only.
  "/pipeline": { tier: "admin", label: "ElecBid Pipeline" },
  // Social Studio: AI content generator + scheduler; posts as the brand → admin/owner.
  "/social": { tier: "admin", label: "Social Studio" },
  "/bookkeeper": { tier: "admin", label: "Bookkeeper" },
  "/payments": { tier: "admin", label: "Payments" },
  // Who gets payment reminders, what they say, and what has gone out. Customer
  // accounts-payable addresses + the switch that e-mails customers → admin/owner.
  "/invoice-reminders": { tier: "admin", label: "Invoice Reminders" },
  // Gemstone raffle ledger for the Shuswap Hospice fundraiser: buyer names,
  // phones, e-mails and addresses, plus Verify/Void → admin/owner only. (The
  // PUBLIC form lives at /raffle, a Vercel rewrite to api/raffle.js — which is
  // why the admin page is a level down.)
  "/raffle/admin": { tier: "admin", label: "Raffle Tickets" },
  "/qb-audit": { tier: "admin", label: "QB Audit" },
  "/coach": { tier: "admin", label: "Coach" },
  "/coach/meetings": { tier: "admin", label: "Coaching Meetings" },
  // Day Audit is per-person computer-activity data and Owner KPIs is company
  // revenue — arguably owner-only, but they are admin-reachable today and
  // tightening them is Dylan's call, not a side effect of this refactor.
  "/coach/day-audit": { tier: "admin", label: "Day Audit" },
  "/coach/rnd": { tier: "admin", label: "R&D Report" },
  "/coach/owner-kpis": { tier: "admin", label: "Owner KPIs" },
  "/coach/sell-rate": { tier: "admin", label: "Selling Rate" },
  "/hiring": { tier: "admin", label: "Hiring" },
  "/autofill": { tier: "admin", label: "Job Auto-Fill" },
  "/jaiman-learning": { tier: "admin", label: "Jai-Man Learning" },
  "/admin": { tier: "admin", label: "Admin" },
  // Templates carry markup_pct / supplier preference — pricing strategy.
  "/quote-templates/:id": { tier: "admin", label: "Quote Template" },

  // — Owner / super.
  "/manage-crew": { tier: "owner", useReal: true, label: "Manage Crew" },
  "/companies": { tier: "super", label: "Companies" },
};

// Nav-facing helper so Layout never re-derives a `show:` expression by hand.
export function navAllows(path, perms) {
  return canAccessPath(path, perms);
}
